import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NewVesselVariables } from '@components/new-vessel-dialog/new-vessel-dialog.component';
import { Shipyard } from 'icreate/common/models/shipyard';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { GenericErrorStrings } from 'icreate/common/validators/validation-errors/generic';
import { vesselValidator } from 'icreate/common/validators/vessel/vessel-validator';

@Component({
    selector: 'app-delete-version-dialog',
    templateUrl: './delete-version-dialog.component.html',
    styleUrls: ['./delete-version-dialog.component.css'],
    standalone: false,
})
export class DeleteVersionDialogComponent implements OnInit {
    readonly NAME = NewVesselVariables.NAME;
    readonly NUMBER = NewVesselVariables.NUMBER;
    readonly DRAWING_ID = NewVesselVariables.DRAWING_ID;
    readonly NUMBER_OF_FCU = NewVesselVariables.NUMBER_OF_FCU;
    readonly NUMBER_OF_OPS = NewVesselVariables.NUMBER_OF_OPS;

    vesselName: string;
    vesselNumber: string;
    drawingId: string;
    numberOps: number = undefined;
    numberFcu: number = undefined;

    validationErrors: ValidationErrorMap = {};
    hasError: boolean;

    numberOpsInvalid: boolean;
    numberFcuInvalid: boolean;
    nameIsRepeated: boolean = false;
    numberIsRepeated: boolean = false;
    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            hasPreviousVersion: boolean;
            vessel: Vessel;
            shipyardList: Shipyard[];
        },
        private dialogRef: MatDialogRef<DeleteVersionDialogComponent>
    ) {}

    ngOnInit(): void {
        this.vesselName = this.data.vessel.name;
        this.vesselNumber = this.data.vessel.number;
        this.drawingId = this.data.vessel.drawingId;
        this.numberOps = this.data.vessel.numberOps;
        this.numberFcu = this.data.vessel.numberFcu;
    }

    deleteVersion() {
        if (!this.data.hasPreviousVersion && this.hasError) return;
        if (this.data.hasPreviousVersion) {
            this.dialogRef.close(true);
            return;
        }
        this.dialogRef.close({
            name: this.vesselName,
            number: this.vesselNumber,
            drawingId: this.drawingId,
            numberOps: this.numberOps,
            numberFcu: this.numberFcu,
        });
    }

    cancel() {
        this.dialogRef.close(false);
    }

    updateNumberOps(numberOps: any) {
        const cleaned = numberOps.replace(/[^\d]/g, '');

        this.numberOps = cleaned === '' ? undefined : Number(cleaned);
        this.numberOpsInvalid = numberOps !== cleaned;
    }

    updateNumberFcu(numberFcu: any) {
        const cleaned = numberFcu.replace(/[^\d]/g, '');

        this.numberFcu = cleaned === '' ? undefined : Number(cleaned);
        this.numberFcuInvalid = numberFcu !== cleaned;
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.NAME) {
            this.vesselName = event.value;
        } else if (event.name === this.NUMBER) {
            this.vesselNumber = event.value;
        } else if (event.name === this.DRAWING_ID) {
            this.drawingId = event.value;
        } else if (event.name === this.NUMBER_OF_FCU) {
            this.updateNumberFcu(event.value);
        } else if (event.name === this.NUMBER_OF_OPS) {
            this.updateNumberOps(event.value);
        }

        this.dialogHasErrors();
    }

    dialogHasErrors() {
        const shipyard = this.data.shipyardList?.find(s => s.id === this.data.vessel.shipyardId);
        const shipyardVessels = shipyard?.vessels || [];

        const numberIsRepeated =
            shipyardVessels.some(v => v.number === this.vesselNumber) && this.data.vessel?.number !== this.vesselNumber;

        if (this.numberIsRepeated !== numberIsRepeated) {
            this.numberIsRepeated = numberIsRepeated;
        }

        const nameIsRepeated =
            shipyardVessels.some(v => v.name === this.vesselName) && this.data.vessel?.name !== this.vesselName;

        if (this.nameIsRepeated !== nameIsRepeated) {
            this.nameIsRepeated = nameIsRepeated;
        }

        const vesselData = {
            shipyardId: this.data.vessel.shipyardId,
            name: this.vesselName,
            number: this.vesselNumber,
            drawingId: this.drawingId,
            numberOps: this.numberOps,
            numberFcu: this.numberFcu,
        };

        const errors = getValidationErrors(vesselValidator(), vesselData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        if (this.numberIsRepeated) {
            mappedErrors['number'] = { message: 'vessel.create.dialog.form.number.repeated' };
        }

        if (this.nameIsRepeated) {
            mappedErrors['name'] = { message: 'vessel.create.dialog.form.name.repeated' };
        }

        if (
            mappedErrors?.numberFcu?.message === GenericErrorStrings.NUMBER_NOT_BELOW ||
            mappedErrors?.numberFcu?.message === GenericErrorStrings.NUMBER_NOT_ABOVE ||
            mappedErrors?.numberFcu?.message === GenericErrorStrings.GENERIC_INVALID_VALUE ||
            this.numberFcuInvalid
        ) {
            mappedErrors['numberFcu'] = { message: 'vessel.create.dialog.form.fcu.range.error' };
        }

        if (
            mappedErrors?.numberOps?.message === GenericErrorStrings.NUMBER_NOT_BELOW ||
            mappedErrors?.numberOps?.message === GenericErrorStrings.NUMBER_NOT_ABOVE ||
            mappedErrors?.numberOps?.message === GenericErrorStrings.GENERIC_INVALID_VALUE ||
            this.numberOpsInvalid
        ) {
            mappedErrors['numberOps'] = { message: 'vessel.create.dialog.form.ops.range.error' };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        const propertiesToCheck = Object.keys(vesselData);
        for (let i = 0; i < propertiesToCheck.length; i++) {
            if (propertiesToCheck[i] in mappedErrors) {
                this.hasError = true;
                return;
            }
        }
        this.hasError = false;
    }

    isCreateDisabled(): boolean {
        return this.hasError;
    }
}
